<template>
  <div class="application-request-list">
    <v-skeleton-loader
      v-if="loading"
      type="card, card"
      class="mb-4 mx-4"
    ></v-skeleton-loader>

    <div
      v-else-if="!requests || requests.length === 0"
      class="text-center py-8"
    >
      <v-icon size="64" color="grey lighten-1">{{ emptyIcon }}</v-icon>
      <div class="text-h6 text-h4 mt-2">{{ emptyTitle }}</div>
      <div v-if="emptyText" class="text-body-2 text-h4">{{ emptyText }}</div>
    </div>

    <template v-else>
      <v-card
        v-for="request in requests"
        :key="request.id"
        :id="`application-request-${request.id}`"
        class="mb-4 mx-4"
        :style="
          $route && $route.query && $route.query.application_id == request.id
            ? 'border: 2px solid #cb0c9f !important;'
            : ''
        "
        outlined
      >
        <v-card-text class="pa-0">
          <div class="d-flex align-center px-4 pt-4">
            <div class="flex-grow-1">
              <div class="d-flex align-center">
                <span class="font-weight-medium text-h5">{{
                  request.applicant.name
                }}</span>
                <v-chip
                  x-medium
                  label
                  :color="REVIEW_STATUS_COLOR[request.review_status]"
                  text-color="white"
                  class="ml-2 px-2"
                >
                  {{ REVIEW_STATUS_TEXT[request.review_status] }}
                </v-chip>
              </div>
              <div class="text-subtitle-2 text-h4 font-weight-bold red--text">
                {{ getLeaveTypeText(request) }}
              </div>
            </div>
            <div class="text-right">
              <div class="text-h6">
                申請日期:
                <formatted-date :date="request.create_time" />
              </div>
            </div>
          </div>

          <v-row
            class="px-4 py-0 my-0"
            v-if="request.apply_type != 4 && request.apply_type != 3 && request.apply_type != 7"
          >
            <v-col cols="4">
              <div class="text-h6">開始時間:</div>
              <div>
                <formatted-date :date="request.start_time" />
              </div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6">結束時間:</div>
              <div>
                <formatted-date :date="request.end_time" />
              </div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6 d-flex align-center">
                <span
                  >期間({{ durationUnit === "hours" ? "小時" : "天" }}):</span
                >
                <v-btn
                  icon
                  x-small
                  color="primary"
                  class="ml-1"
                  @click="toggleDurationUnit"
                  title="切換單位"
                >
                  <v-icon x-small>mdi-swap-horizontal</v-icon>
                </v-btn>
              </div>
              <div>
                {{ formatLeaveDuration(request) }}
              </div>
            </v-col>
          </v-row>
          <!-- 離職申請 (apply_type 7) -->
          <v-row
            class="px-4 py-0 my-0"
            v-else-if="request.apply_type === 7"
          >
            <v-col cols="12">
              <div class="text-h6">生效時間:</div>
              <div>
                <formatted-date v-if="request.start_time" :date="request.start_time" format-string="yyyy-MM-dd" />
                <span v-if="request.start_time"> 24:00</span>
                <span v-else>-</span>
              </div>
            </v-col>
          </v-row>
          <!-- 調班申請 (apply_type 3) -->
          <v-row
            class="px-4 py-0 my-0"
            v-else-if="request.apply_type === 3 && request.extra"
          >
            <v-col cols="4" v-if="request.extra.from">
              <div class="text-h6">原班別:</div>
              <div>
                {{ request.extra.from.date }}
                {{ request.extra.from.name || request.extra.from.code }}
                <div
                  v-if="
                    request.extra.from.start_time && request.extra.from.end_time
                  "
                  class="text-caption"
                >
                  ({{ request.extra.from.start_time }} ~
                  {{ request.extra.from.end_time }})
                </div>
              </div>
            </v-col>
            <v-col cols="4" v-if="request.extra.to">
              <div class="text-h6">調班目標:</div>
              <div>
                {{ request.extra.to.date }}
                {{ request.extra.to.name || request.extra.to.code }}
                <div
                  v-if="
                    request.extra.to.start_time && request.extra.to.end_time
                  "
                  class="text-caption"
                >
                  ({{ request.extra.to.start_time }} ~
                  {{ request.extra.to.end_time }})
                </div>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6 d-flex align-center">
                <span
                  >期間({{ durationUnit === "hours" ? "小時" : "天" }}):</span
                >
                <v-btn
                  icon
                  x-small
                  color="primary"
                  class="ml-1"
                  @click="toggleDurationUnit"
                  title="切換單位"
                >
                  <v-icon x-small>mdi-swap-horizontal</v-icon>
                </v-btn>
              </div>
              <div>
                {{ formatLeaveDuration(request) }}
              </div>
            </v-col>
          </v-row>
          <!-- 補打卡 -->
          <template v-else-if="request.extra">
            <v-row class="px-4 py-0 my-0">
              <v-col cols="4" v-if="request.extra.clock_in_actual">
                <div class="text-h6">實際上班打卡:</div>
                <div>
                  {{ formatDateTime(request.extra.clock_in_actual) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.clock_out_actual">
                <div class="text-h6">實際下班打卡:</div>
                <div>
                  {{ formatDateTime(request.extra.clock_out_actual) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.lunch_break_start_actual">
                <div class="text-h6">實際午休開始:</div>
                <div>
                  {{ formatDateTime(request.extra.lunch_break_start_actual) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.lunch_break_end_actual">
                <div class="text-h6">實際午休結束:</div>
                <div>
                  {{ formatDateTime(request.extra.lunch_break_end_actual) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.rest_1_start_actual">
                <div class="text-h6">實際休息一開始:</div>
                <div>
                  {{ formatDateTime(request.extra.rest_1_start_actual) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.rest_1_end_actual">
                <div class="text-h6">實際休息一結束:</div>
                <div>
                  {{ formatDateTime(request.extra.rest_1_end_actual) }}
                </div>
              </v-col>
            </v-row>
            <v-row class="px-4 py-0 my-0">
              <v-col
                cols="4"
                v-if="
                  request.extra.is_overtime === 'true' ||
                  request.extra.is_overtime === true
                "
              >
                <div class="text-h6 red--text">是否加班:</div>
                <div>是</div>
              </v-col>
              <v-col cols="4" v-if="request.extra.start_time">
                <div class="text-h6">加班開始:</div>
                <div>
                  {{ formatDateTime(request.extra.start_time) }}
                </div>
              </v-col>
              <v-col cols="4" v-if="request.extra.end_time">
                <div class="text-h6">加班結束:</div>
                <div>
                  {{ formatDateTime(request.extra.end_time) }}
                </div>
              </v-col>
              <v-col
                cols="4"
                v-if="request.extra.start_time && request.extra.end_time"
              >
                <div class="text-h6">加班時數:</div>
                <div>
                  {{ getOvertimeDuration(request.extra) }}
                </div>
              </v-col>
            </v-row>
          </template>

          <v-row class="px-4 py-0 my-0">
            <v-col cols="4">
              <div class="text-h6">事由:</div>
              <div v-if="editingRequest && editingRequest.id === request.id">
                <v-text-field
                  v-model="editingRequest.reason"
                  dense
                  hide-details
                  outlined
                ></v-text-field>
              </div>
              <div v-else>
                <span>{{ request.reason || "-" }}</span>
              </div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6">附件:</div>
              <div
                v-if="request.attachmentFiles && request.attachmentFiles.length"
              >
                <div class="d-flex flex-wrap">
                  <a
                    v-for="(file, idx) in request.attachmentFiles"
                    :key="idx"
                    class="mr-2"
                    :href="buildAttachmentHref(file)"
                    target="_blank"
                    rel="noopener"
                  >
                    {{
                      typeof file === "string"
                        ? "附件" + (idx + 1)
                        : file.name || "附件" + (idx + 1)
                    }}
                  </a>
                </div>
              </div>
              <div v-else-if="request.attachment">
                <a
                  :href="
                    typeof request.attachment === 'string'
                      ? request.attachment
                      : request.attachment.url || request.attachment.link
                  "
                  target="_blank"
                  rel="noopener"
                >
                  {{
                    typeof request.attachment === "string"
                      ? "附件"
                      : request.attachment.name || "附件"
                  }}
                </a>
              </div>
              <div v-else-if="request.attachment_url">
                <a :href="request.attachment_url" target="_blank" rel="noopener"
                  >附件</a
                >
              </div>
              <div v-else>-</div>
            </v-col>
            <v-col cols="4">
              <div class="text-h6">拒絕原因:</div>
              <div v-if="editingRequest && editingRequest.id === request.id">
                <v-text-field
                  v-model="editingRequest.reject_reason"
                  dense
                  hide-details
                  outlined
                ></v-text-field>
              </div>
              <div v-else>
                <span>{{ request.reject_reason || "-" }}</span>
              </div>
            </v-col>
          </v-row>
          <v-row
            class="px-4 py-0 my-0"
            v-if="
              (request.apply_type === 2 ||
                (request.extra && request.extra.is_overtime)) &&
              request.extra &&
              request.extra.reward_selection
            "
          >
            <v-col cols="4">
              <div class="text-h6">加班補償:</div>
              <div>
                <span>{{ getRewardSelectionDisplay(request.extra) }}</span>
              </div>
            </v-col>
          </v-row>
          <!-- row for extra -->
          <v-row
            class="px-4 py-0 my-0"
            v-if="request.extra && request.extra.status"
          >
            <v-col cols="4">
              <div class="text-h6">狀態:</div>
              <div>
                <span>{{ request.extra.status }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- View/Edit Mode Buttons -->
          <v-row class="px-4 py-2 my-0" v-if="mode === 'view' && canEdit">
            <v-col cols="12" class="d-flex justify-end">
              <div v-if="editingRequest && editingRequest.id === request.id">
                <v-btn
                  small
                  color="success"
                  class="mr-2"
                  @click="saveEdit()"
                  :loading="saving"
                >
                  <v-icon small left>mdi-content-save</v-icon>
                  儲存
                </v-btn>
                <v-btn
                  small
                  color="grey"
                  @click="cancelEdit"
                  :disabled="saving"
                >
                  <v-icon small left>mdi-close</v-icon>
                  取消
                </v-btn>
              </div>
              <v-btn v-else small color="primary" @click="startEdit(request)">
                <v-icon small left>mdi-pencil</v-icon>
                編輯
              </v-btn>
            </v-col>
          </v-row>

          <!-- View Mode Revoke Button for approved future leave -->
          <v-row
            v-if="mode === 'view' && isRevokable(request)"
            class="px-4 pb-4 my-0"
          >
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                outlined
                color="purple"
                class="mx-2"
                :loading="request.processing"
                :disabled="request.processing"
                @click="showRevokeConfirmation(request)"
              >
                <v-icon left size="16" color="purple"
                  >mdi-backspace-reverse</v-icon
                >
                <span class="purple--text">撤銷</span>
              </v-btn>
            </v-col>
          </v-row>

          <!-- Review Mode Buttons (no revoke in review tab) -->
          <div v-if="mode === 'review'" class="d-flex px-4 pb-4">
            <v-btn
              outlined
              color="red"
              class="mx-2"
              :loading="request.processing"
              :disabled="request.processing"
              @click="showApproveConfirmation(request)"
            >
              <v-icon left size="16" color="red">mdi-check</v-icon>
              <span class="red--text">同意</span>
            </v-btn>
            <v-btn
              outlined
              color="green"
              class="mx-2"
              :loading="request.processing"
              :disabled="request.processing"
              @click="showRejectConfirmation(request)"
            >
              <v-icon left size="16" color="green">mdi-close</v-icon>
              <span class="green--text">拒絕</span>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <!-- Sentinel for Infinite Scroll -->
    <div
      id="sentinel-application-list"
      class="d-flex justify-center py-4"
      v-show="!loading && requests.length > 0"
    >
      <v-progress-circular
        v-if="loadingMore"
        indeterminate
        color="#cb0c9f"
        size="24"
      ></v-progress-circular>
    </div>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="showConfirmDialog" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ confirmTitle }}</v-card-title>
        <v-card-text>
          <div class="mb-3">{{ confirmText }}</div>
          <v-checkbox
            v-if="
              !requireReason &&
              confirmRequest &&
              confirmRequest.extra &&
              confirmRequest.extra.overtime_reward_policy === 'either'
            "
            v-model="reject_compensatory_leave"
            dense
            hide-details
            label="不允許換補休"
          />
          <v-textarea
            v-if="requireReason"
            outlined
            color="rgba(0,0,0,.6)"
            v-model="confirmReason"
            class="
              font-size-input
              border
              text-light-input
              border-radius-md
              mt-2
            "
            :rules="[(v) => !!v || '請輸入拒絕理由']"
            required
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            @click="showConfirmDialog = false"
            >取消</v-btn
          >
          <v-btn
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="onConfirm"
            >確定</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Info Dialog for error messages -->
    <info-dialog
      :visible.sync="showInfoDialog"
      :title="infoDialogTitle"
      :message="infoDialogMessage"
      yes-button-text="確定"
    />
  </div>
</template>

<script>
import FormattedDate from "@/components/FormattedDate.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import {
  REVIEW_STATUS,
  REVIEW_STATUS_TEXT,
  REVIEW_STATUS_COLOR,
  LEAVE_TYPE,
  APPLY_TYPE,
} from "@/constants";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import { formatLeaveDurationText } from "@/utils/attendance";
import { mapGetters } from "vuex";

export default {
  name: "ApplicationRequestList",
  components: {
    FormattedDate,
    InfoDialog,
  },
  props: {
    requests: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyIcon: {
      type: String,
      default: "mdi-calendar-blank",
    },
    emptyTitle: {
      type: String,
      default: "目前沒有表單",
    },
    emptyText: {
      type: String,
      default: "",
    },
    mode: {
      type: String,
      default: "view", // 'view' or 'review'
      validator: (v) => ["view", "review"].includes(v),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    loadingMore: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      REVIEW_STATUS,
      REVIEW_STATUS_TEXT,
      REVIEW_STATUS_COLOR,
      editingRequest: null,
      saving: false,
      observer: null,
      // Confirmation dialog
      showConfirmDialog: false,
      confirmAction: null,
      confirmRequest: null,
      confirmTitle: "",
      confirmText: "",
      confirmReason: "",
      requireReason: false,
      reject_compensatory_leave: false,
      // Info dialog
      showInfoDialog: false,
      infoDialogTitle: "",
      infoDialogMessage: "",
      durationUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    ...mapGetters({ userRole: "user/userRole" }),
  },
  mounted() {
    this.initIntersectionObserver();
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDurationUnit
    );
  },
  watch: {},
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDurationUnit
    );
  },
  methods: {
    isRevokable(request) {
      if (!request) return false;
      if (request.review_status !== REVIEW_STATUS.APPROVED) return false;
      if (this.userRole === "superadmin") return true;
      if (!request.start_time) return false;
      const start = new Date(request.start_time);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      return start > todayEnd;
    },

    // get leave_type text from LEAVE_TYPE constant
    getLeaveTypeText(request) {
      let leave_type = request.leave_type;
      let apply_type = request.apply_type;
      if (apply_type === 1) {
        return LEAVE_TYPE[leave_type] || leave_type;
      }
      return APPLY_TYPE[apply_type] || "請假申請";
    },
    getRewardSelectionDisplay(extra) {
      if (!extra) return "-";
      const raw = extra.reward_selection;
      if (raw == null) return "-";

      const labelMap = {
        pay: "加班費",
        comp: "補休假",
      };

      const toLabels = (items) =>
        items
          .map((s) => String(s).trim())
          .filter((s) => s.length)
          .map((code) => labelMap[code] || code);

      if (Array.isArray(raw)) return toLabels(raw).join(", ");

      if (typeof raw === "string") {
        const trimmed = raw.trim();
        // Example: "[pay, comp]" => ["pay","comp"]
        if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
          const inner = trimmed.slice(1, -1);
          const parts = inner
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length);
          return toLabels(parts).join(", ");
        }
        // Example: "pay, comp" or "pay" => map to labels
        const parts = trimmed
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s.length);
        return toLabels(parts).join(", ");
      }

      return labelMap[String(raw)] || String(raw);
    },
    formatLeaveDuration(request) {
      return formatLeaveDurationText(request, this.durationUnit);
    },
    syncDurationUnit() {
      this.durationUnit =
        localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDurationUnit() {
      this.durationUnit = this.durationUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.durationUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    getOvertimeDuration(extra) {
      if (extra.overtime_minutes) {
        return `${Math.floor(extra.overtime_minutes / 60)}小時${
          extra.overtime_minutes % 60
        }分`;
      }
      if (extra.start_time && extra.end_time) {
        const start = new Date(extra.start_time);
        const end = new Date(extra.end_time);
        const diff = (end - start) / 1000 / 60; // minutes
        if (diff > 0) {
          const h = Math.floor(diff / 60);
          const m = Math.floor(diff % 60);
          return `${h}小時${m > 0 ? `${m}分` : ""}`;
        }
      }
      return "-";
    },
    buildAttachmentHref(file) {
      if (!file) return "#";

      const normalize = (value) => {
        if (!value && value !== 0) return null;
        let stringValue = typeof value === "string" ? value : String(value);
        stringValue = stringValue.trim();
        if (!stringValue) return null;
        if (/^https?:\/\//i.test(stringValue)) {
          return stringValue;
        }

        const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");

        const normalizedValue = stringValue.replace(/^\/+/, "");
        if (normalizedValue.startsWith("resource/attachments/")) {
          return `${base}/${normalizedValue}`;
        }

        return `${base}/resource/attachments/${normalizedValue}`;
      };

      if (typeof file === "string") {
        const href = normalize(file);
        return href || "#";
      }

      const direct = normalize(file.url || file.link);
      if (direct) {
        return direct;
      }

      const identifier =
        file.filename || file.file_name || file.file || file.id;

      const href = normalize(identifier);
      return href || "#";
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    startEdit(request) {
      this.editingRequest = Object.assign({}, request);
    },
    cancelEdit() {
      this.editingRequest = null;
    },
    async saveEdit() {
      if (!this.editingRequest) return;
      this.saving = true;
      try {
        await this.updateRequest(this.editingRequest);
        this.editingRequest = null;
      } catch (e) {
        // Error handling is done in updateRequest
      } finally {
        this.saving = false;
      }
    },
    // Show confirmation dialog for approve action
    showApproveConfirmation(request) {
      this.confirmTitle = "核准申請";
      this.confirmText = `確定要核准 ${
        request.applicant.name
      } 的 ${this.getLeaveTypeText(request)} 申請單嗎？`;
      this.confirmRequest = request;
      this.confirmAction = this.approveLeaveRequest;
      this.requireReason = false;
      this.confirmReason = "";
      this.reject_compensatory_leave = Boolean(
        request?.extra?.reject_compensatory_leave
      );
      this.showConfirmDialog = true;
    },

    // Approve a leave request
    async approveLeaveRequest(request) {
      // Close the confirmation dialog
      this.showConfirmDialog = false;

      // Set the request to loading state using $set to ensure reactivity
      this.$set(request, "processing", true);

      try {
        // Follow the same style as fetchLeaveRequests using axios
        // POST /api/applications/{id}/approve with body { review_status: 2 }
        const payload = {
          review_status: REVIEW_STATUS.APPROVED,
        };
        if (request?.extra?.overtime_reward_policy === "either") {
          payload.reject_compensatory_leave = this.reject_compensatory_leave;
        }
        await this.$axios.post(
          `/api/applications/${request.id}/approve`,
          payload
        );

        // Update the request status in the UI using $set to ensure reactivity
        this.$set(request, "review_status", REVIEW_STATUS.APPROVED);
        // display success dialog
        this.infoDialogTitle = "核准成功";
        this.infoDialogMessage = `${
          request.applicant.name
        } 的 ${this.getLeaveTypeText(request)} 申請單已核准`;
        this.showInfoDialog = true;

        this.$emit("remove", request.id);
      } catch (error) {
        console.error("Error approving leave request:", error);

        // Show error message in InfoDialog if it exists
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          this.infoDialogTitle = "核准失敗";
          this.infoDialogMessage = error.response.data.message;
          this.showInfoDialog = true;
        } else {
          this.infoDialogTitle = "核准失敗";
          this.infoDialogMessage = "核准申請時發生錯誤，請稍後再試。";
          this.showInfoDialog = true;
        }
      } finally {
        // Set processing to false using $set to ensure reactivity
        this.$set(request, "processing", false);
      }
    },

    // Show confirmation dialog for reject action
    showRejectConfirmation(request) {
      this.confirmTitle = "拒絕申請";
      this.confirmText = `確定要拒絕 ${
        request.applicant.name
      } 的 ${this.getLeaveTypeText(request)} 申請單嗎？`;
      this.confirmRequest = request;
      this.confirmAction = this.rejectLeaveRequest;
      this.requireReason = true;
      this.confirmReason = "";
      this.showConfirmDialog = true;
    },

    showRevokeConfirmation(request) {
      if (!this.isRevokable(request)) return;
      this.confirmTitle = "撤銷申請";
      this.confirmText = `確定要撤銷 ${
        request.applicant.name
      } 的 ${this.getLeaveTypeText(request)} 申請單嗎？`;
      this.confirmRequest = request;
      this.confirmAction = this.revokeLeaveRequest;
      this.requireReason = true;
      this.confirmReason = "";
      this.showConfirmDialog = true;
    },

    async revokeLeaveRequest(request) {
      // Close the confirmation dialog
      this.showConfirmDialog = false;

      // Set the request to loading state
      this.$set(request, "processing", true);

      try {
        await this.$axios.post(`/api/applications/${request.id}/revoke`, {
          reject_reason: this.confirmReason,
        });

        // Update status locally
        this.$set(request, "review_status", REVIEW_STATUS.REVOKED);
        this.$set(request, "reject_reason", this.confirmReason);

        this.infoDialogTitle = "撤銷成功";
        this.infoDialogMessage = `${
          request.applicant.name
        } 的 ${this.getLeaveTypeText(request)} 申請單已撤銷`;
        this.showInfoDialog = true;
      } catch (error) {
        console.error("Error revoking leave request:", error);

        this.infoDialogTitle = "撤銷失敗";
        this.infoDialogMessage =
          error?.response?.data?.message ||
          error.message ||
          "撤銷申請時發生錯誤，請稍後再試。";
        this.showInfoDialog = true;
      } finally {
        this.$set(request, "processing", false);
      }
    },

    onConfirm() {
      if (this.requireReason && !this.confirmReason) {
        // simple validation: prevent confirm without reason
        return;
      }
      if (this.confirmAction) {
        this.confirmAction(this.confirmRequest);
      }
    },

    // Reject a leave request
    async rejectLeaveRequest(request) {
      // Close the confirmation dialog
      this.showConfirmDialog = false;

      // Set the request to loading state using $set to ensure reactivity
      this.$set(request, "processing", true);

      try {
        // Attempt to reject via API using axios
        await this.$axios.post(`/api/applications/${request.id}/reject`, {
          review_status: REVIEW_STATUS.REJECTED,
          reject_reason: this.confirmReason,
        });

        // Update the request status in the UI using $set to ensure reactivity
        this.$set(request, "review_status", REVIEW_STATUS.REJECTED);

        this.$emit("remove", request.id);

        // show info dialog
        this.infoDialogTitle = "拒絕成功";
        this.infoDialogMessage = `${
          request.applicant.name
        } 的 ${this.getLeaveTypeText(request)} 申請單已拒絕`;
        this.showInfoDialog = true;
      } catch (error) {
        console.error("Error rejecting leave request:", error);

        // Show error message in InfoDialog if it exists
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          this.infoDialogTitle = "拒絕失敗";
          this.infoDialogMessage = error.response.data.message;
          this.showInfoDialog = true;
        } else {
          this.infoDialogTitle = "拒絕失敗";
          this.infoDialogMessage = "拒絕申請時發生錯誤，請稍後再試。";
          this.showInfoDialog = true;
        }

        // Set processing to false using $set to ensure reactivity
        this.$set(request, "processing", false);
      }
    },

    async updateRequest(request) {
      try {
        const payload = {
          reason: request.reason,
          reject_reason: request.reject_reason,
        };

        const response = await this.$axios.put(
          `/api/applications/${request.id}`,
          payload
        );

        // Update the request in the list
        // Since requests is a prop, we should ideally emit an update
        // But since we are reusing the logic, we can try to update the object reference
        // which might update the prop in parent if it's the same object.
        // Or we can emit an event.

        // Find the request string in local prop? props are not directly mutable but objects are.
        const originalRequest = this.requests.find((r) => r.id === request.id);
        if (originalRequest) {
          Object.assign(originalRequest, payload);
        }

        this.infoDialogTitle = "更新成功";
        this.infoDialogMessage = response.data.message || "申請單已更新";
        this.showInfoDialog = true;
      } catch (error) {
        console.error("Error updating application:", error);
        this.infoDialogTitle = "更新失敗";
        this.infoDialogMessage =
          error?.response?.data?.message ||
          error.message ||
          "更新申請單時發生錯誤";
        this.showInfoDialog = true;
        throw error;
      }
    },
    initIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        const target = entries[0];
        if (target.isIntersecting && this.hasMore && !this.loadingMore) {
          this.$emit("load-more");
        }
      }, options);

      const sentinel = this.$el.querySelector("#sentinel-application-list");
      if (sentinel) {
        this.observer.observe(sentinel);
      }
    },
  },
};
</script>

<style scoped>
/* Scoped styles if needed */
</style>
